import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { TerminalSocket, type ConnStatus } from './ws-client';
import { esc, tab, arrow, ctrlByte, type ArrowDir } from './keys';
import { keyboardOffset } from './viewport';
import { selectedTheme, THEME_CHANGE_EVENT } from './themes';

// A single toolbar key. `send` returns the bytes to feed the PTY, or null for
// keys (Ctrl) that toggle state instead of emitting bytes immediately.
interface KeySpec {
  label: string;
  send: (() => Uint8Array) | null;
  sticky?: boolean;
}

const KEYS: KeySpec[] = [
  { label: 'Esc', send: esc },
  { label: 'Tab', send: tab },
  { label: 'Ctrl', send: null, sticky: true },
  { label: '↑', send: () => arrowBytes('up') },
  { label: '↓', send: () => arrowBytes('down') },
  { label: '←', send: () => arrowBytes('left') },
  { label: '→', send: () => arrowBytes('right') },
  { label: '^C', send: () => new Uint8Array([ctrlByte('c')]) },
  { label: '^D', send: () => new Uint8Array([ctrlByte('d')]) },
  { label: '^Z', send: () => new Uint8Array([ctrlByte('z')]) },
  { label: '^L', send: () => new Uint8Array([ctrlByte('l')]) },
];

function arrowBytes(dir: ArrowDir): Uint8Array {
  return arrow(dir);
}

const STATUS_LABEL: Record<ConnStatus, string> = {
  connecting: 'Connecting…',
  connected: 'Connected',
  reconnecting: 'Reconnecting…',
  offline: 'Offline',
};

export function openTerminal(parent: HTMLElement, sessionId: string): () => void {
  // Wrap the terminal so the key toolbar can sit beneath it within the same
  // flex column without overlapping the scrollable buffer.
  const host = document.createElement('div');
  host.className = 'term-host';
  const toolbar = document.createElement('div');
  toolbar.className = 'key-toolbar';
  parent.append(host, toolbar);

  const term = new Terminal({
    cursorBlink: true,
    fontSize: 14,
    convertEol: false,
    theme: selectedTheme().xterm,
  });
  // Re-theme the live buffer when the user picks a new scheme from the list.
  const onThemeChange = () => {
    term.options.theme = selectedTheme().xterm;
  };
  window.addEventListener(THEME_CHANGE_EVENT, onThemeChange);
  const fit = new FitAddon();
  term.loadAddon(fit);
  term.open(host);
  fit.fit();

  const statusEl = document.getElementById('conn-status');
  const setStatus = (s: ConnStatus) => {
    if (!statusEl) return;
    statusEl.textContent = STATUS_LABEL[s];
    statusEl.dataset.status = s;
  };

  const sock = new TerminalSocket(sessionId, {
    onOutput: (bytes) => term.write(bytes),
    onExit: () => term.write('\r\n\x1b[33m[session exited]\x1b[0m\r\n'),
    onStatus: setStatus,
  });

  // Ctrl is a sticky modifier: tapping it arms the next typed letter to be sent
  // as a control code, then auto-clears.
  let ctrlArmed = false;
  let ctrlBtn: HTMLButtonElement | null = null;
  const setCtrl = (armed: boolean) => {
    ctrlArmed = armed;
    ctrlBtn?.classList.toggle('active', armed);
  };

  for (const key of KEYS) {
    const btn = document.createElement('button');
    btn.className = 'key';
    btn.type = 'button';
    btn.textContent = key.label;
    if (key.sticky) {
      ctrlBtn = btn;
      btn.classList.add('key-sticky');
      btn.onclick = () => setCtrl(!ctrlArmed);
    } else {
      btn.onclick = () => {
        sock.sendInput(key.send!());
        term.focus();
      };
    }
    toolbar.append(btn);
  }

  const enc = new TextEncoder();
  term.onData((d) => {
    if (ctrlArmed && d.length === 1) {
      const c = d.toLowerCase();
      // Best-effort: a-z become control codes; anything else is sent as typed.
      if (c >= 'a' && c <= 'z') {
        sock.sendInput(new Uint8Array([ctrlByte(c)]));
      } else {
        sock.sendInput(enc.encode(d));
      }
      setCtrl(false);
      return;
    }
    sock.sendInput(enc.encode(d));
  });

  // layout floats the toolbar just above the on-screen keyboard and keeps the
  // xterm rows in sync. The toolbar is `position: fixed`, so we:
  //  1. lift it by the keyboard height (0 when closed → sits at screen bottom),
  //  2. pad the terminal container by the toolbar's height so buffer content is
  //     never hidden behind the floating bar, then
  //  3. re-fit so xterm recomputes cols/rows for the reduced area.
  const vv = window.visualViewport;
  const layout = () => {
    const off = vv
      ? keyboardOffset({ innerHeight: window.innerHeight, vvHeight: vv.height, vvOffsetTop: vv.offsetTop })
      : 0;
    toolbar.style.bottom = `${off}px`;
    // Drop the safe-area-inset padding while the keyboard is open: the keyboard
    // itself already clears the home indicator.
    toolbar.classList.toggle('kb-open', off > 0);
    parent.style.paddingBottom = `${toolbar.offsetHeight}px`;
    fit.fit();
    sock.sendResize(term.cols, term.rows);
  };
  window.addEventListener('resize', layout);
  vv?.addEventListener('resize', layout);
  vv?.addEventListener('scroll', layout);
  setTimeout(layout, 50);

  return () => {
    window.removeEventListener(THEME_CHANGE_EVENT, onThemeChange);
    window.removeEventListener('resize', layout);
    vv?.removeEventListener('resize', layout);
    vv?.removeEventListener('scroll', layout);
    parent.style.paddingBottom = '';
    sock.close();
    term.dispose();
    host.remove();
    toolbar.remove();
  };
}
